import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { isEmpty } from 'cng-common/helpers/validation/isEmpty'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { updateInvalidData } from '../formSlice'
import { createValidationMessageSelector } from '../helpers'
import { IUseOnValidate } from '../models'

/* This hook returns a validate handler function */
export const useHandleValidate = <T>({
  onValidate,
  onEmptyCheck = isEmpty,
  formKey,
  field,
  isDisabled,
  isRequired,
  isReadOnly,
  isVisible = true,
}: IUseOnValidate<T>) => {
  const dispatch = useAppDispatch()
  const selectValidationMessage = useMemo(
    () => createValidationMessageSelector(formKey, field),
    [formKey, field]
  )
  const validationMessage = useSelector(selectValidationMessage)

  const handleValidate = useCallback(
    async (value: T): Promise<string> => {
      if ((!isDisabled && !isReadOnly && isVisible) || validationMessage) {
        let newValidationMessage = validationMessage

        newValidationMessage = isRequired ? onEmptyCheck(value) : ''

        if (!(isRequired && newValidationMessage) && onValidate) {
          newValidationMessage = await onValidate(value)
        }
        if (newValidationMessage === validationMessage) {
          return newValidationMessage
        }

        dispatch(
          updateInvalidData({ key: formKey, field, validationMessage: newValidationMessage })
        )

        return newValidationMessage
      }
      return ''
    },
    [
      isDisabled,
      isReadOnly,
      isVisible,
      validationMessage,
      isRequired,
      onEmptyCheck,
      onValidate,
      dispatch,
      formKey,
      field,
    ]
  )

  useEffect(() => {
    if (!isVisible) {
      dispatch(updateInvalidData({ key: formKey, field, validationMessage: '' }))
    }
  }, [dispatch, field, formKey, isVisible])

  return { handleValidate }
}
